import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  CatalogOffering,
  CatalogService,
  CatalogWarehouse,
  CatalogWarehouseBin,
  InventoryMovement,
  InventoryMovementPayload
} from './catalog.service';

interface OptionItem {
  label: string;
  value: string;
}

@Component({
  standalone: true,
  selector: 'ain-inventory-internal-consumption',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, FormsModule, ButtonModule, InputTextModule, SelectModule, TableModule],
  template: `
    <ain-page [title]="t('catalog.internalConsumption.title')" [description]="t('catalog.internalConsumption.description')">
      <ain-inventory-page-hint pageKey="internalConsumption" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="form-card">
          <h3>{{ t('catalog.internalConsumption.createTitle') }}</h3>
          <div class="form-grid">
            <label>
              <span>{{ t('catalog.inventory.offeringId') }}</span>
              <p-select [(ngModel)]="form.offeringId" [options]="offeringOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.warehouseId') }}</span>
              <p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.movements.bin') }}</span>
              <p-select [(ngModel)]="form.binId" [options]="binOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.quantity') }}</span>
              <input pInputText type="number" min="0" step="0.001" [(ngModel)]="form.quantity" />
            </label>
            <label>
              <span>{{ t('catalog.inventory.reference') }}</span>
              <input pInputText [(ngModel)]="form.referenceNumber" />
            </label>
            <label>
              <span>{{ t('catalog.movements.idempotencyKey') }}</span>
              <div class="inline-actions">
                <input pInputText [(ngModel)]="form.idempotencyKey" />
                <button
                  pButton
                  type="button"
                  severity="secondary"
                  [label]="t('catalog.movements.generateKey')"
                  (click)="generateIdempotencyKey()"></button>
              </div>
            </label>
          </div>
          <div class="actions">
            <button pButton type="button" [label]="t('common.save')" (click)="submit()"></button>
          </div>
        </section>

        <section class="list-card">
          <h3>{{ t('catalog.internalConsumption.historyTitle') }}</h3>
          <p-table [value]="consumptions()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.reference') }}</th>
                <th>{{ t('catalog.inventory.offeringId') }}</th>
                <th>{{ t('catalog.inventory.warehouseId') }}</th>
                <th>{{ t('catalog.movements.bin') }}</th>
                <th>{{ t('catalog.inventory.quantity') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('catalog.inventory.occurredAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.referenceNumber || '-' }}</td>
                <td>{{ resolveOfferingLabel(row.lines?.[0]?.offeringId) }}</td>
                <td>{{ resolveWarehouseLabel(row.lines?.[0]?.fromWarehouseId) }}</td>
                <td>{{ resolveBinLabel(row.lines?.[0]?.fromBinId) }}</td>
                <td>{{ row.lines?.[0]?.quantity || '-' }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.processedAtUtc }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .form-card, .list-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; background: #fff; }
    .form-card h3, .list-card h3 { margin: 0 0 10px; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid span { color: #475569; font-size: 12px; font-weight: 600; }
    .inline-actions { display: flex; gap: 8px; }
    .actions { margin-top: 10px; display: flex; gap: 8px; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class InventoryInternalConsumptionComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly consumptions = signal<InventoryMovement[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);

  readonly form = {
    offeringId: '',
    warehouseId: '',
    binId: '',
    quantity: 1,
    referenceNumber: '',
    idempotencyKey: ''
  };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.generateIdempotencyKey();
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.service.listOfferings().subscribe({
      next: offerings => {
        this.offerings.set(offerings.filter(item => item.inventoryEnabled));
        if (!this.form.offeringId && this.offerings().length > 0)
          this.form.offeringId = this.offerings()[0].id;

        this.service.listWarehouses().subscribe({
          next: warehouses => {
            this.warehouses.set(warehouses.filter(item => item.isActive));
            if (!this.form.warehouseId && this.warehouses().length > 0)
              this.form.warehouseId = this.warehouses()[0].id;

            this.service.listWarehouseBins().subscribe({
              next: bins => {
                this.bins.set(bins.filter(item => item.isActive));
                this.service.listInventoryMovements('internal-consumption').subscribe({
                  next: movements => {
                    this.consumptions.set(movements);
                    this.loading.set(false);
                  },
                  error: () => {
                    this.error.set(this.t('catalog.internalConsumption.loadFailed'));
                    this.loading.set(false);
                  }
                });
              },
              error: () => {
                this.error.set(this.t('catalog.internalConsumption.loadFailed'));
                this.loading.set(false);
              }
            });
          },
          error: () => {
            this.error.set(this.t('catalog.internalConsumption.loadFailed'));
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.error.set(this.t('catalog.internalConsumption.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  submit(): void {
    this.error.set('');
    if (!this.form.offeringId || !this.form.warehouseId || this.form.quantity <= 0 || !this.form.idempotencyKey.trim()) {
      this.error.set(this.t('catalog.internalConsumption.requiredFields'));
      return;
    }

    const payload: InventoryMovementPayload = {
      offeringId: this.form.offeringId,
      quantity: Number(this.form.quantity),
      warehouseId: this.form.warehouseId,
      binId: this.form.binId || null,
      fromWarehouseId: null,
      fromBinId: null,
      toWarehouseId: null,
      toBinId: null,
      idempotencyKey: this.form.idempotencyKey.trim(),
      referenceNumber: this.form.referenceNumber.trim()
    };

    this.service.postInventoryMovement('internal-consumption', payload).subscribe({
      next: () => {
        this.generateIdempotencyKey();
        this.form.referenceNumber = '';
        this.load();
      },
      error: () => this.error.set(this.t('catalog.internalConsumption.submitFailed'))
    });
  }

  offeringOptions(): OptionItem[] {
    return this.offerings().map(item => ({ label: `${item.code} - ${item.title}`, value: item.id }));
  }

  warehouseOptions(): OptionItem[] {
    return this.warehouses().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  binOptions(): OptionItem[] {
    if (!this.form.warehouseId)
      return [];

    return this.bins()
      .filter(item => item.warehouseId === this.form.warehouseId)
      .map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  resolveOfferingLabel(offeringId: string | null | undefined): string {
    if (!offeringId) return '-';
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  resolveWarehouseLabel(warehouseId: string | null | undefined): string {
    if (!warehouseId) return '-';
    const warehouse = this.warehouses().find(item => item.id === warehouseId);
    return warehouse ? `${warehouse.code} - ${warehouse.name}` : warehouseId;
  }

  resolveBinLabel(binId: string | null | undefined): string {
    if (!binId) return '-';
    const bin = this.bins().find(item => item.id === binId);
    return bin ? `${bin.code} - ${bin.name}` : binId;
  }

  generateIdempotencyKey(): void {
    this.form.idempotencyKey = crypto.randomUUID();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
